import {
  RedDayCriteria,
  NonProductionDay,
} from "us.collection.admin/interfaces";
import {
  NonProductionDayTypeInterface,
  RedDayCriterias,
  RedDay,
  RedDayWithCriteria,
  RedDayCriteriaType,
  RedDayCriteriaMap,
  NON_PROD_DAY_STATUS_LABELS,
  NonProdDayStatus,
} from "us.collection.admin/constants";
import { Guid } from "guid-typescript";
import dayjs from "dayjs";
import { ColumnFilterTypes } from "us.common/components/antd/TableTree/Constants";

/**
 * @function
 * @description add or update red days to current redDays state
 * @param {Array<NonProductionDay>} redDays current redDays
 * @param {NonProductionDay} day new or existing day
 * @returns  {Array<NonProductionDay>}
 */
export const addOrUpdateRedDays = (
  redDays: Array<NonProductionDay>,
  day: NonProductionDay,
  uniqueKey: Guid,
): Array<NonProductionDay> => {
  try {
    const { key } = day;
    if (!key.isEmpty()) {
      let isMatched = false;
      redDays = redDays.map((redDay: NonProductionDay) => {
        if (key.equals(redDay.key)) {
          isMatched = true;
          return day;
        } else {
          return redDay;
        }
      });
      if (!isMatched) {
        redDays.push(day);
      }
    } else {
      redDays.push({ ...day, key: uniqueKey });
    }
    return [...redDays];
  } catch (error) {
    return [...redDays];
  }
};

/**
 * @function
 * @description get Criteria array object
 * @param {NonProductionDayTypeInterface} selectedCriteria selected RedDayCriterias
 * @returns {Array<RedDayCriteria>}
 */
export const getRedDayCriteria = (
  selectedCriteria: NonProductionDayTypeInterface,
): Array<RedDayCriteria> => {
  try {
    return RedDayCriterias.map(({ type, value }: RedDayCriteria) => ({
      type,
      value: type == RedDayCriteriaMap[selectedCriteria] ? true : false,
    }));
  } catch (error) {
    return RedDayCriterias;
  }
};

/**
 * @function
 * @description get formatted red days
 * @param {RedDayResponse} nonProductionDays response params
 * @returns {Array<RedDayCriteria>}
 */
export const getFormattedRedDays = (
  nonProductionDays: Array<RedDay>,
): Array<RedDayWithCriteria> => {
  try {
    if (Array.isArray(nonProductionDays)) {
      return nonProductionDays.map((day) => ({
        id: day.id,
        startDate: day.startDate,
        endDate: day.endDate,
        reason: day.reason,
        isFixedDate: day.isFixedDate,
        nonEhf: day.isNonEhfDay,
        nonSms: day.isNonSMSDay,
        nonPrinting: day.isNonPrintDay,
        nonEmail: day.isNonEmailDay,
        status: day.status,
        isRecurring: day.isRecurring,
        modifiedBy: day.modifiedBy,
        modifiedDate: day.modifiedDate,
      }));
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @function
 * @description get Red Day InitialValues
 * @param {NonProductionDayTypeInterface} selectedCriteria selected criteria
 * @returns
 */
export const getRedDayInitialValues = (
  selectedCriteria: NonProductionDayTypeInterface,
  selectedYear: number,
): NonProductionDay => {
  try {
    const currentYear = dayjs().year();
    const tomorrow = dayjs().add(1, "day");
    const selectedYearStartDate = `${selectedYear}-01-01`;
    const startOfYear = dayjs(selectedYearStartDate);
    return {
      id: -1,
      key: Guid.createEmpty(),
      dateRange:
        currentYear == selectedYear
          ? [tomorrow, tomorrow]
          : [startOfYear, startOfYear],
      reason: "",
      isFixedDate: false,
      criteria: getRedDayCriteria(selectedCriteria),
    };
  } catch (error) {
    return {
      id: -1,
      key: Guid.createEmpty(),
      dateRange: [],
      reason: "",
      isFixedDate: false,
      criteria: [],
    };
  }
};

/**
 * @function
 * @description remove red day
 * @param {Array<NonProductionDay>} redDays all redDays
 * @param {number} removeDayId day Id
 * @returns {Array<NonProductionDay>}
 */
export const removeRedDays = (
  redDays: Array<NonProductionDay>,
  removeDayId: Guid,
): Array<NonProductionDay> => {
  try {
    return redDays.filter(
      ({ key }: NonProductionDay) => !key.equals(removeDayId),
    );
  } catch (error) {
    return redDays;
  }
};

/**
 * @function
 * @description add or update red days to current redDays state
 * @param {Array<RedDay>} redDays all redDays
 * @param {RedDay} day new or existing day
 * @returns {NonProductionDataType}
 */
export const addOrUpdateRedDayRelevantCriteria = (
  redDays: Array<RedDay>,
  day: NonProductionDay,
): Array<RedDay> => {
  try {
    const criteriaMap = day.criteria.reduce((acc, curr) => {
      acc[curr.type] = curr.value;
      return acc;
    }, {} as Record<string, boolean>);

    const redDay: RedDay = {
      id: day.id,
      startDate: day.dateRange[0]?.toString() || "",
      endDate: day.dateRange[1]?.toString() || "",
      reason: day.reason,
      isFixedDate: day.isFixedDate,
      isNonEhfDay: criteriaMap["nonEhf"] || false,
      isNonSMSDay: criteriaMap["nonSms"] || false,
      isNonPrintDay: criteriaMap["nonPrinting"] || false,
      isNonEmailDay: criteriaMap["nonEmail"] || false,
      status: day.status || 0,
      isRecurring: day.isRecurring || false,
      modifiedBy: day.modifiedBy || "",
      modifiedDate: day.modifiedDate || "",
    };
    redDays.push(redDay);
    return redDays;
  } catch (error) {
    return redDays;
  }
};

/**
 * @description - get Criteria array object based on red day details
 * @param {RedDay} day red day details
 * @returns {Array<RedDayCriteria>}
 */
export const redDayToCriteria = (
  day: RedDay,
  selectedCriteria: NonProductionDayTypeInterface,
): Array<RedDayCriteria> => {
  try {
    return RedDayCriterias.map(({ type, value }: RedDayCriteria) => {
      let c = {
        type,
        value:
          type == RedDayCriteriaType.Non_EHF
            ? day.isNonEhfDay
            : type == RedDayCriteriaType.Non_SMS
            ? day.isNonSMSDay
            : type == RedDayCriteriaType.Non_Printing
            ? day.isNonPrintDay
            : day.isNonEmailDay,
      };
      if (type == selectedCriteria) {
        c.value = true;
      }
      return c;
    });
  } catch (error) {
    return RedDayCriterias;
  }
};

/**
 * @description - Helper function to filter Non Production Days table based on column filters and search value
 * @param {Object} params function params
 * @param {Function} params.t translation function
 * @param {string} params.currentDateFormat current date format to apply for date columns
 * @returns {Function} filter function to be passed to TableTree component
 */
export const handleNonProductionDaysTableFilter = (params: {
  t: (key: any) => any;
  currentDateFormat?: string;
}): ((searchData: any, dataSource: any[], filters?: any[]) => any[]) => {
  const { t, currentDateFormat } = params;
  const dateFormat = currentDateFormat || "YYYY-MM-DD";

  return (searchData: any, dataSource: any[], filters?: any[]): any[] => {
    try {
      const list = Array.isArray(dataSource) ? dataSource : [];

      const isGrouped = list.some((r: any) => Array.isArray(r?.children));

      // For grouped data (TableTree with parent rows + children), never run search on
      // the parent rows directly (they won't have the filtered fields).
      // Instead, filter/search the children and keep parents that have matching children.
      if (!Array.isArray(filters) || filters.length === 0) {
        if (!isGrouped) {
          return typeof searchData === "function" ? searchData(list) : list;
        }

        const result: any[] = [];
        list.forEach((parent: any) => {
          const nextChildren =
            typeof searchData === "function"
              ? searchData(parent?.children ?? [])
              : parent?.children ?? [];
          if (nextChildren?.length) {
            result.push({
              ...parent,
              children: nextChildren,
            });
          }
        });
        return result;
      }

      const normalize = (v: any) => (v ?? "").toString().trim().toLowerCase();
      const includes = (haystack: any, needle: any) =>
        normalize(haystack).includes(normalize(needle));

      // get display text for status column based on status value
      const getStatusText = (status: any) => {
        const key =
          NON_PROD_DAY_STATUS_LABELS[status as unknown as NonProdDayStatus];
        return key ? (t(key) as any) : "";
      };

      // get display text for recurring column based on boolean value
      const getRecurringText = (isRecurring: any) =>
        isRecurring ? (t("US.COMMON:COMMON.YES") as any) : "-";

      // function to check if a row matches the filter criteria
      const matchFilter = (row: any, f: any) => {
        const val = f?.val;
        if (
          val === null ||
          typeof val === "undefined" ||
          `${val}`.trim() === ""
        ) {
          return true;
        }

        const dataIndex = f?.dataIndex;
        const type = f?.type;

        if (dataIndex === "status") {
          const statusText = getStatusText(row?.status);
          return includes(statusText, val) || includes(row?.status, val);
        }

        if (dataIndex === "isRecurring") {
          const recurringText = getRecurringText(row?.isRecurring);
          const n = normalize(val);
          if (
            n === "-" ||
            n.includes("-") ||
            n.includes("no") ||
            n.includes("false")
          ) {
            return !row?.isRecurring;
          }
          return (
            includes(recurringText, val) || includes(row?.isRecurring, val)
          );
        }

        if (type === ColumnFilterTypes.DATE) {
          return dayjs(row?.[dataIndex])
            .format(dateFormat)
            ?.includes(val?.toString());
        }

        if (type === ColumnFilterTypes.DATE_TIME) {
          return dayjs(row?.[dataIndex])
            .format(`${dateFormat}, HH:mm`)
            ?.includes(val?.toString());
        }

        if (type === ColumnFilterTypes.AMOUNT) {
          return row?.[dataIndex]?.toString()?.includes(val?.toString());
        }

        return includes(row?.[dataIndex], val);
      };

      // function to apply all filters to a list of rows
      const applyFiltersToList = (rows: any[]) => {
        const rowsList = Array.isArray(rows) ? rows : [];
        return rowsList.filter((row) =>
          filters.every((f) => matchFilter(row, f)),
        );
      };

      // if data is not grouped, apply filters directly to the list
      if (!isGrouped) {
        return applyFiltersToList(list);
      }

      // if data is grouped, apply filters to children and keep parents with matching children
      const result: any[] = [];
      list.forEach((parent: any) => {
        const nextChildren = applyFiltersToList(parent?.children);
        if (nextChildren?.length) {
          result.push({
            ...parent,
            children: nextChildren,
          });
        }
      });
      return result;
    } catch (error) {
      return [];
    }
  };
};
